// src/pages/UpdatePassword.tsx
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

type UrlAuthStatus =
  | "checking"
  | "ready"
  | "invalid_or_expired"
  | "missing_tokens"
  | "wrong_flow";

export default function UpdatePassword() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<UrlAuthStatus>("checking");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Detecta params de error típicos del link (query o hash)
  const urlErrorInfo = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    const errorFromUrl = searchParams.get("error") || hashParams.get("error") || "";
    const errorCode = searchParams.get("error_code") || hashParams.get("error_code") || "";
    const errorDesc =
      searchParams.get("error_description") || hashParams.get("error_description") || "";

    return { errorFromUrl, errorCode, errorDesc };
  }, []);

  // Tokens pueden venir en hash, o puede venir "code" (PKCE)
  const tokenInfo = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    const access_token = hashParams.get("access_token") || "";
    const refresh_token = hashParams.get("refresh_token") || "";
    const type = hashParams.get("type") || searchParams.get("type") || "";
    const code = searchParams.get("code") || "";

    const hasHashTokens = Boolean(access_token && refresh_token);
    const hasCode = Boolean(code);

    return { access_token, refresh_token, type, code, hasHashTokens, hasCode };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initSessionFromUrl = async () => {
      setError("");
      setMessage("");

      // 0) Blindaje: esta pantalla es SOLO para recovery
      // Si viene un type explícito y NO es recovery, no seguimos.
      if (tokenInfo.type && tokenInfo.type !== "recovery") {
        if (!isMounted) return;
        setStatus("wrong_flow");
        setError("Este enlace no corresponde a recuperación de contraseña.");
        return;
      }

      // 1) Si el link ya viene con error, mostrarlo de forma amigable
      if (urlErrorInfo.errorFromUrl || urlErrorInfo.errorCode) {
        const code = (urlErrorInfo.errorCode || "").toLowerCase();
        if (code.includes("otp_expired")) {
          if (!isMounted) return;
          setStatus("invalid_or_expired");
          setError("El enlace expiró. Vuelve a solicitar uno nuevo.");
          return;
        }

        if (!isMounted) return;
        setStatus("invalid_or_expired");
        setError("El enlace no es válido o ya fue usado. Solicita uno nuevo.");
        return;
      }

      // 2) Si tenemos tokens en hash → setSession
      if (tokenInfo.hasHashTokens) {
        const { error } = await supabase.auth.setSession({
          access_token: tokenInfo.access_token,
          refresh_token: tokenInfo.refresh_token,
        });

        if (!isMounted) return;

        if (error) {
          setStatus("invalid_or_expired");
          setError("El enlace no es válido o ya expiró. Vuelve a solicitar uno nuevo.");
          return;
        }

        setStatus("ready");
        return;
      }

      // 3) Si viene code (PKCE) → exchangeCodeForSession
      if (tokenInfo.hasCode) {
        const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);

        if (!isMounted) return;

        if (error) {
          setStatus("invalid_or_expired");
          setError("El enlace expiró o no es válido. Vuelve a solicitar uno nuevo.");
          return;
        }

        setStatus("ready");
        return;
      }

      // 4) Si no hay tokens ni code, pero ya hay sesión activa, también sirve
      const { data } = await supabase.auth.getSession();

      if (!isMounted) return;

      if (data.session) {
        setStatus("ready");
        return;
      }

      // 5) Nada de nada → la abrieron manual sin link
      setStatus("missing_tokens");
    };

    initSessionFromUrl();

    return () => {
      isMounted = false;
    };
  }, [tokenInfo, urlErrorInfo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (status !== "ready") {
      setError("Necesitas abrir esta página desde un enlace válido de recuperación.");
      return;
    }

    if (!password || !confirm) {
      setError("Por favor completa ambos campos.");
      return;
    }

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({ password });

    setLoading(false);

    if (error) {
      console.error("updateUser password error:", error);
      const msg = (error.message || "").toLowerCase();

      if (msg.includes("jwt") || msg.includes("expired") || msg.includes("session")) {
        setError("El enlace expiró o no es válido. Vuelve a solicitar un nuevo enlace.");
      } else {
        setError("No se pudo actualizar la contraseña. Intenta de nuevo.");
      }
      return;
    }

    setMessage("Tu contraseña se actualizó correctamente.");

    setTimeout(() => {
      navigate("/login", { replace: true });
    }, 1200);
  };

  return (
    <div className="relative min-h-screen bg-[#0b0b0f] flex items-center justify-center px-6 text-white overflow-hidden">
      {/* Glow background */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="w-full max-w-md">
        {/* Volver al inicio */}
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition"
          type="button"
        >
          <span aria-hidden>←</span> Volver al inicio
        </button>

        <div className="w-full bg-[#111114]/80 p-10 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-sm">
          {/* Header marca */}
          <div className="text-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              Ovylon
              <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
            </div>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              Escribe tu nueva contraseña para continuar tu flujo de creación.
            </p>
          </div>

          {/* Flow incorrecto */}
          {status === "wrong_flow" && (
            <div className="mb-6 rounded-xl border border-yellow-400/20 bg-yellow-500/10 p-4 text-sm text-yellow-200">
              {error || "Este enlace no corresponde a recuperación de contraseña."}
              <div className="mt-2 text-[12px] text-yellow-200/80">
                Si acabas de confirmar tu email, vuelve al login e inicia sesión.
              </div>
              <div className="mt-3 flex gap-2">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition text-xs text-white"
                >
                  Ir al login
                </Link>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition text-xs text-white"
                >
                  Recuperar contraseña
                </Link>
              </div>
            </div>
          )}

          {/* Estado de link inválido */}
          {status === "missing_tokens" && (
            <div className="mb-6 rounded-xl border border-yellow-400/20 bg-yellow-500/10 p-4 text-sm text-yellow-200">
              Parece que abriste esta página sin un enlace válido de recuperación.
              <div className="mt-2 text-[12px] text-yellow-200/80">
                Vuelve a solicitar el enlace desde “Olvidé mi contraseña”.
              </div>
              <div className="mt-3">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition text-xs text-white"
                >
                  Ir a recuperar contraseña
                </Link>
              </div>
            </div>
          )}

          {status === "invalid_or_expired" && (
            <div className="mb-6 rounded-xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-200">
              {error || "El enlace no es válido o ya expiró. Solicita uno nuevo."}
              <div className="mt-3">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition text-xs text-white"
                >
                  Solicitar nuevo enlace
                </Link>
              </div>
            </div>
          )}

          {/* Form */}
          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Nueva contraseña</label>
              <input
                type="password"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={status !== "ready" || loading}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">Confirmar contraseña</label>
              <input
                type="password"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                placeholder="Repite tu contraseña"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                disabled={status !== "ready" || loading}
              />
            </div>

            {error && status === "ready" && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            {message && <p className="text-emerald-300 text-sm text-center">{message}</p>}

            {/* Botón principal estilo indigo */}
            <button
              type="submit"
              disabled={loading || status !== "ready"}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Actualizando..." : "Actualizar contraseña"}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-indigo-300 hover:underline text-sm">
                Volver al login
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          Si el enlace expiró, solicita uno nuevo desde “Olvidé mi contraseña”.
        </p>
      </div>
    </div>
  );
}

